import { StackScreenProps } from '@react-navigation/stack'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Platform, ScrollView, TextInput, View } from 'react-native'
import Toast from 'react-native-root-toast'
import {
  ArticleEditor,
  ArticleEditorRef,
} from '../article-editor/article-editor'
import { Toolbar } from '../article-editor/toolbar'
import { Cluster, useClusterByIdQuery } from '../../../../generated/graphql'
import { useKeyboard } from '../../../../hooks/keyboard.hook'
import { AppNavigatorParams } from '../../../../navigation/types'
import { MessageType } from '../../../../utils/message-type'
import Row from '../../../../@ui/containers/grid/row'
import LoadingFullScreen from '../../../../@ui/containers/grid/full-screen.spinner'
import { ScreenWrapper } from '../../../../@ui/containers/grid/screen.wrapper'
import { HelperText } from 'react-native-paper'
import { LLMProvider } from './llm/llm.provider'
import { createStyle } from '../../../../contexts/global-styles.provider'
import { LLMDropdown } from './llm/llm.dropdown'
import { LLMModal } from './llm/llm.modal'
import { FixBottomBar } from './components/fix-bottom.bar'
import { useForm, UseFormReturn } from 'react-hook-form'
import { MelddTextInput } from '../../../../@ui/form/meldd-input'
import { useIsMobileDevice } from '../../../../hooks/is-mobile.hook'
import { useLLMIntegration } from './hooks/perpective-llm.hook'
import { useLLMState } from './llm/llm.provider'
import { useArticleCreation } from './hooks/create-article.hook'
import { useArticleHeader } from './hooks/article-header.hook'
import { alignBrowserWidth } from '../../../../utils/browser'
import { Link } from '../../../../@ui/typo/link.typo'

export type FormShape = {
  minCoRank: string
  isAutoTitle: boolean
  title: string
}

interface LLMControlsProps {
  disabled: boolean
  onOpenLLMModal: () => void
}

function LLMControls({ disabled, onOpenLLMModal }: LLMControlsProps) {
  const { t } = useTranslation('articles')
  const { state: llmState } = useLLMState()

  return (
    <Row justifyContent="space-between">
      <View style={{ flex: 1 }}>
        <LLMDropdown />
      </View>

      <View style={{ alignItems: 'flex-end', marginLeft: 10 }}>
        <Link
          text={t('use-ai')}
          onPress={onOpenLLMModal}
          disabled={disabled}
          isLoading={llmState.loading}
        />
      </View>
    </Row>
  )
}

function ArticleForm({ form }: { form: UseFormReturn<FormShape> }) {
  const { t } = useTranslation('articles')
  const inputRef = useRef<TextInput>(null)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  return (
    <>
      <Row alignItems="center" columnGap={4}>
        <View style={{ flex: 1 }}>
          <MelddTextInput
            ref={inputRef}
            form={form}
            name="title"
            label={t('articles:your-title')}
          />
        </View>
        <View style={{ width: 120 }}>
          <MelddTextInput
            form={form}
            name="minCoRank"
            keyboardType="numeric"
            label={t('articles:min-co-rank')}
          />
        </View>
      </Row>
      <Row>
        <HelperText type={'info'} style={{ paddingHorizontal: 0 }}>
          {t('articles:min-co-rank-description')}
        </HelperText>
      </Row>
    </>
  )
}

function CreateManualArticle({
  onCreatedArticle,
  cluster,
  initialValues,
}: {
  onCreatedArticle: (articleId: string) => void
  cluster: Cluster
  initialValues: {
    content?: string
    model?: string
  }
}) {
  const editorRef = useRef<ArticleEditorRef>(null)
  const isMobile = useIsMobileDevice()
  const { keyboardDisplayed } = useKeyboard()
  const styles = useEditorStyle()
  const [editorState, setEditorState] = useState<{
    [action: string]: { active: boolean; enabled: boolean }
  }>({})

  // Initialize form
  const form = useForm<FormShape>({
    defaultValues: {
      minCoRank: '0',
      title: '',
    },
  })

  // Initialize LLM integration
  const llmIntegration = useLLMIntegration(cluster, editorRef, initialValues)

  // Initialize article creation
  const articleCreation = useArticleCreation(
    cluster,
    form,
    editorRef,
    onCreatedArticle
  )

  // Initialize header
  useArticleHeader(
    cluster,
    () => {
      editorRef.current?.requestContent().then(({ html, text }) => {
        articleCreation.handleCreateArticle(html, text)
      })
    },
    articleCreation.loading
  )

  const articleFocusResponsive =
    Platform.OS === 'web' &&
    keyboardDisplayed &&
    !llmIntegration.llmState.state.loading
  const bottomBarHeight = articleFocusResponsive ? 0 : 60

  // Calculate proper padding: bottom bar height + safe area bottom inset
  // Web needs significant extra padding due to different rendering behavior
  const editorPaddingBottom = articleFocusResponsive
    ? 0
    : bottomBarHeight + (isMobile ? 0 : 0)

  const handleMenuItem = (msg: any) => {
    editorRef.current?.onMenuItem(msg)
  }

  return (
    <ScreenWrapper
      withScrollView={false}
      style={{
        marginHorizontal: 10,
        paddingTop: 10,
      }}
    >
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ flexGrow: 1, paddingBottom: bottomBarHeight }}
        keyboardShouldPersistTaps="handled"
        stickyHeaderIndices={[1]}
      >
        <ArticleForm form={form} />
        <View style={styles.stickyToolbarWrapper}>
          <Toolbar editorState={editorState} onMenuItem={handleMenuItem} />
        </View>
        <View style={[styles.editor, { paddingBottom: editorPaddingBottom }]}>
          <ArticleEditor
            ref={editorRef}
            editorMode={'edit'}
            bottomBarOffset={bottomBarHeight}
            onEditorStateChange={setEditorState}
          />
        </View>
      </ScrollView>

      <LLMModal
        ref={llmIntegration.llmModalRef}
        onGenerate={llmIntegration.handleGenerate}
      />

      <FixBottomBar height={bottomBarHeight}>
        {!articleFocusResponsive && (
          <>
            <View style={{ marginTop: 10 }} />
            <LLMControls
              disabled={articleCreation.loading}
              onOpenLLMModal={llmIntegration.openLLMModal}
            />
            <View style={{ marginTop: 10 }} />
          </>
        )}
      </FixBottomBar>
    </ScreenWrapper>
  )
}

export default function ({
  navigation,
  route,
}: StackScreenProps<AppNavigatorParams, 'CreateArticle'>) {
  const { clusterId } = route.params
  const { t } = useTranslation('articles')
  const { data: clusterData } = useClusterByIdQuery({
    variables: { id: clusterId },
  })

  const cluster = useMemo(
    () => clusterData?.clusterById as Cluster,
    [clusterData]
  )

  const initialValues = useMemo(() => {
    return {
      title: route.params?.title,
      content: route.params?.content,
      model: route.params?.model,
    }
  }, [route.params])

  useEffect(() => {
    if (!clusterId) {
      Toast.show(
        t('No cluster found. Please refresh the app.'),
        MessageType.error
      )
      navigation.pop()
    }
  }, [])

  if (!cluster) return <LoadingFullScreen />

  return (
    <LLMProvider>
      <CreateManualArticle
        cluster={cluster}
        initialValues={initialValues}
        onCreatedArticle={() => {
          navigation.replace('ClusterCards', { clusterId: clusterId })
        }}
      />
    </LLMProvider>
  )
}

const useEditorStyle = createStyle(({ theme }) => ({
  container: {},

  stickyToolbarWrapper: {
    backgroundColor: theme.colors?.background ?? '#fff',
    zIndex: 10,
  },

  editor: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
}))
